import {useEffect, useRef, useState, useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';
import Utility from './utilityfunctions';
import env from './env';
import _ from 'lodash';

/**
 * Writes a line in the debug console each time the components re-renders, adding information about which props change caused the render.
 * (meant for internal debugging only. DO NOT USE IN PRODUCTION).
 * @param {string} name Component name used when logging the message.
 * @param {object} props props used in the component.
 */
export function useWhyDidYouUpdate(name, props) {
	// Get a mutable ref object where we can store props ...
	// ... for comparison next time this hook runs.
	const previousProps = useRef();

	useEffect(() => {
		if (!env.isProd()) {
			if (previousProps.current) {
				// Get all keys from previous and current props
				const allKeys = Object.keys({...previousProps.current, ...props});
				// Use this object to keep track of changed props
				const changesObj = {};
				// Iterate through keys
				allKeys.forEach((key) => {
					// If previous is different from current
					if (previousProps.current[key] !== props[key]) {
						// Add to changesObj
						changesObj[key] = {
							from: previousProps.current[key],
							to: props[key]
						};
					}
				});

				// If changesObj not empty then output to console
				if (Object.keys(changesObj).length) {
					console.log('[why-did-you-update]', name, changesObj);
				}
			}

			// Finally update previousProps with current props for next hook call
			previousProps.current = props;
		}
	});
}

/**
 * Retrieves the user preferences from the redux store.
 * @returns {Object} The user preferences.
 */
export function useUserPreferences() {
	return useSelector((state) => state.userPreferences);
}

/**
 * Returns the current Engagement Id.
 * @returns {Number | String} The current Engagement ID.
 */
export function useEngagementId() {
	let engagementId = useSelector((state) => Number(state.engagement.engagementId));
	if (!engagementId) {
		engagementId = Utility.getEngagementIdFromUrl(window);
		engagementId = Number(engagementId);
	}
	return engagementId;
}

/**
 * Returns the current Engagement.
 * @returns {Object} The current Engagement.
 */
export function useEngagement() {
	return useSelector((state) => state.engagement);
}

/**
 * Returns the current Engagement Metadata.
 * @param {Number} engagementId The Engagement ID.
 * @returns {Object} The current Engagement Metadata.
 */
export function useEngagementMetadata(engagementId) {
	const metadata = useSelector((state) => state.engagementMetaData);
	if (metadata.totalpageditems === 0) {
		return {datacenterid: 0};
	} else {
		return metadata?.find((x) => x.engagementid === engagementId) ?? {datacenterid: 0};
	}
}

/**
 * Returns the current configurations
 * @returns {Object} the current Configurations
 */
export function useConfigurations() {
	return useSelector((state) => state.configurations);
}

export function useEngagementIsMest() {
	return useSelector((state) => state.engagement.isMultiEntityEngagement || false);
}

/**
 * Returns the application state.
 * @returns {Boolean} The application state.
 */
export function useAppState() {
	return useSelector((state) => state.apponlinestate);
}

/**
 * Returns the document's User Presence.
 * @param {String} documentId The Document ID.
 * @returns {Object | String} The document's User Presence.
 */
export function useUserPresence(documentId) {
	return useSelector((state) => state.userPresence[documentId]);
}

/**
 * Returns the current task's Id.
 * @returns {String} The current task's Id.
 */
export function useTaskId() {
	return useSelector((state) => state.taskId);
}

/*
 * Handles a key value formed object state, handy for form state management.
 * @param {object} initialState An initial key value formed state object.
 * @returns {Array} Returns a stateful value, and a function to update it.
 */
export function useForm(initialState = {}) {
	const [form, setForm] = useState(initialState);
	return [form, useCallback((value) => setForm((state) => ({...state, ...value})), [])];
}

export function useAresTaskBuildMilestoneId() {
	const taskBuildMilestoneId = useSelector((state) => state.quickLink?.guidedWorkflowTaskBuildMilestoneId);

	return taskBuildMilestoneId;
}

export function useIsSuperModalOpen(isSuperModalOpen) {
	const verticalNavigation = useSelector((state) => state.verticalNavigation);

	if (!_.isEmpty(verticalNavigation)) {
		return verticalNavigation.isOpen;
	} else {
		const pathname = window.location.pathname.toLowerCase();
		return pathname.startsWith('/buildsteps') || isSuperModalOpen;
	}
}

export function useTrialBalanceId() {
	return Utility.getTrialBalanceIdFromUrl(window);
}

export function useAccountAndDisclosureDocumentId() {
	return useSelector((state) => state.quickLink?.accountLevelRiskAssessmentDocumentId);
}

export function useQuery() {
	const {search} = useLocation();

	const searchParams = useMemo(() => new URLSearchParams(search), [search]);

	let queryStringParameters = {};
	for (const [name, value] of searchParams.entries()) {
		if (name.toLowerCase() !== 'referrer') {
			queryStringParameters[name.toLowerCase()] = value;
		}
	}

	return queryStringParameters;
}

export function useDocumentTasks(documentId, parentName) {
	let result = [];
	const documentTasks = useSelector((state) => {
		const doc = state.documentList[parentName].response?.data?.[documentId];
		const tasks = state.documentList[parentName].tasks?.byId;
		if (!doc?.tasks || !Array.isArray(doc.tasks)) {
			return [];
		}

		if (Object.keys(tasks).length === 0) {
			return Object.keys(tasks)
				.filter((key) => doc.tasks.includes(key))
				.map((key) => tasks[key]);
		}
	});

	if (documentTasks && documentTasks.length > 0) {
		result = documentTasks;
	}
	return result;
}

export function useBlur(shouldBlur = false, className, elementId) {
	const [blur, setBlur] = useState(false);

	useEffect(() => {
		if (!shouldBlur) {
			return;
		}

		const documentContainers = window.document.querySelectorAll(`[id=${elementId}]`);

		if (documentContainers && documentContainers.length > 0) {
			if (blur) {
				documentContainers.forEach((container) => {
					container.classList.add(className);
				});
			} else {
				documentContainers.forEach((container) => {
					container.classList.remove(className);
				});
			}
		}
	}, [blur]);

	return setBlur;
}
